import axios, { AxiosInstance } from 'axios';

/**
 * Funnel v2 API client.
 *
 * Anonymous endpoints work without a session. Authenticated endpoints
 * (qualify*) read the access token from localStorage('okey_access_token').
 */
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000';
const TOKEN_KEY = 'okey_access_token';

export function setAccessToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

const client: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  timeout: 10_000,
});

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export type FunnelEvent =
  | 'landing_view'
  | 'lead_captured'
  | 'signup_started'
  | 'signup_completed'
  | 'qualify_step'
  | 'qualify_completed'
  | 'onboarding_started'
  | 'onboarding_completed'
  | 'tour_completed';

export interface FunnelEventInput {
  event: FunnelEvent;
  step?: string;
  payload?: Record<string, unknown>;
  utm?: Record<string, unknown>;
  email?: string;
  user_id?: string;
}

export interface QualifyPatch {
  user_role?: string;
  company_size?: string;
  monthly_invoice_volume?: string;
  current_erp?: string[];
  primary_pain_point?: string;
  preferred_locale?: 'es' | 'en' | 'ca';
  wants_sales_call?: boolean;
  phone?: string;
}

export interface QualifyProfile {
  user_role: string | null;
  company_size: string | null;
  monthly_invoice_volume: string | null;
  current_erp: string[] | null;
  primary_pain_point: string | null;
  preferred_locale: string | null;
  wants_sales_call: boolean | null;
  qualified_at: string | null;
}

export async function trackEvent(payload: FunnelEventInput): Promise<void> {
  try {
    await client.post('/api/funnel/event', payload);
  } catch {
    // Never throw — analytics must not break the UX.
  }
}

export async function captureLead(input: {
  email: string;
  locale?: string;
  utm?: Record<string, unknown>;
  source?: string;
}): Promise<{ ok: boolean; lead_id: string | null }> {
  const { data } = await client.post('/api/funnel/lead', input);
  return data;
}

export async function patchQualify(patch: QualifyPatch): Promise<QualifyProfile> {
  const { data } = await client.patch('/api/auth/me/qualify', patch);
  return data;
}

export async function completeQualify(): Promise<QualifyProfile & { email: string }> {
  const { data } = await client.post('/api/auth/me/qualify/complete', {});
  return data;
}

const ATTR_COOKIE = '_okey_attr';

export function readAttributionCookie(): Record<string, string> | null {
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${ATTR_COOKIE}=`));
    if (!match) return null;
    const raw = decodeURIComponent(match.split('=').slice(1).join('='));
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : null;
  } catch {
    return null;
  }
}

export function readQueryUtm(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const url = new URL(window.location.href);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
    for (const key of keys) {
      const v = url.searchParams.get(key);
      if (v) out[key] = v;
    }
  } catch {
    /* ignore */
  }
  return out;
}

export function gatherAttribution(): Record<string, unknown> {
  const cookie = readAttributionCookie();
  if (cookie && Object.keys(cookie).length > 0) return cookie;
  return readQueryUtm();
}
